import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
  type RawHistoryPoint,
} from "../../pages/reports/utils/trackingHistoryWindows";
import { scoreDriving, type DrivingScore, type ScoreSettings } from "../../pages/reports/utils/drivingScore";

export interface BehaviorResult {
  deviceId: string;
  vehicleName: string;
  score: DrivingScore | null;
  loading: boolean;
  error: string | null;
}

// Los puntos crudos fuera del estado de zustand: si cambian los ajustes se
// recalifica al instante sin volver a pedir el historial (lo lento).
const rawByDevice = new Map<string, RawHistoryPoint[]>();

/** Puntos ya leídos de una unidad (para el «Informe del vehículo» por semana, sin volver a pedirlos). */
export const behaviorPointsOf = (deviceId: string): RawHistoryPoint[] => rawByDevice.get(deviceId) ?? [];

interface ReportBehaviorState {
  results: Record<string, BehaviorResult>;
  order: string[];
  loading: boolean;
  tz: string | null;
  loadRange: (
    devices: { deviceId: string; vehicleName: string }[],
    from: string,
    to: string,
    settings: ScoreSettings,
  ) => Promise<void>;
  rescore: (settings: ScoreSettings) => void;
  clear: () => void;
}

export const useReportBehaviorStore = create<ReportBehaviorState>()((set, get) => ({
  results: {},
  order: [],
  loading: false,
  tz: null,

  loadRange: async (devices, from, to, settings) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const windows = buildWindows(new Date(from).getTime(), new Date(to).getTime());
    rawByDevice.clear();
    const initial: Record<string, BehaviorResult> = {};
    devices.forEach((d) => {
      initial[d.deviceId] = { ...d, score: null, loading: true, error: null };
    });
    set({ results: initial, order: devices.map((d) => d.deviceId), loading: true, tz });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(d.deviceId, windows, tz);
      rawByDevice.set(d.deviceId, points);
      const score = scoreDriving(points, settings);
      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              score,
              loading: false,
              error:
                points.length === 0 && failedChunks > 0
                  ? "No se pudo leer el historial (servicio no disponible)."
                  : failedChunks > 0
                    ? `${failedChunks} tramo(s) con error: la calificación puede estar incompleta`
                    : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  rescore: (settings) => {
    const results = { ...get().results };
    for (const id of Object.keys(results)) {
      const pts = rawByDevice.get(id);
      if (pts) results[id] = { ...results[id], score: scoreDriving(pts, settings) };
    }
    set({ results });
  },

  clear: () => {
    rawByDevice.clear();
    set({ results: {}, order: [], loading: false, tz: null });
  },
}));
